<template lang="pug">
pre.bq-ascii(translate="no" role="img" :aria-label="label" :style="fontSize ? { fontSize } : null") {{ shown }}
</template>

<script>
// Module scope, not <script setup>: defineProps is hoisted out of the
// setup function, so a prop default cannot name a binding declared there.
// Exported as well, for anyone who wants the art without the animation.
// The Skapi wordmark, this component's default art. String.raw keeps every
// backslash as typed (no escapes to maintain; the text holds no "#{" or
// backtick) and the pre element renders it verbatim.
export const SKAPI_BANNER = String.raw`
          ##\                          ##\
          ## |                         \__|
 #######\ ## |  ##\ ######\   ######\  ##\
##  _____|## | ##  |\____##\ ##  __##\ ## |
\######\  ######  / ####### |## /  ## |## |
 \____##\ ##  _##< ##  __## |## |  ## |## |
#######  |## | \##\\####### |#######  |## |
\_______/ \__|  \__|\_______|##  ____/ \__|
                             ## |
                             ## |
                             \__|`.slice(1);

// Where the press splits the default art into letters (S K A P I). They
// touch, so no blank column marks the seams and they are listed here.
export const SKAPI_BOUNDS = [0, 10, 19, 29, 39, 44];
</script>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

// Text art that animates: one sandstorm pass writes it onto an empty grid
// when it first appears, then it idles through three motions in random order
// until the component goes away. Everything is a plain text swap on this one
// element at about 20 fps, and only while a motion runs.

const props = defineProps({
    /** The art to animate. */
    text: { type: String, default: SKAPI_BANNER },
    /** Announced in place of the art, which reads as noise. */
    label: { type: String, default: "Skapi" },
    /** Sweep the art in with one sandstorm pass when it first appears. */
    reveal: { type: Boolean, default: true },
    /** Keep cycling the idle motions after that. */
    loop: { type: Boolean, default: true },
    /** Which idle motions to draw from: "storm", "press", "jitter". One name
        or a list of them, so a single name repeats that motion. Unknown
        names are ignored, and a list with none left falls back to all three. */
    motions: { type: [Array, String], default: () => ["storm", "press", "jitter"] },
    /** Milliseconds of stillness between one motion and the next, the reveal
        included. */
    restMs: { type: Number, default: 3200 },
    /** A fixed size, "8px" say, in place of the responsive default. Set
        inline so it wins wherever the stylesheets land. */
    fontSize: { type: String, default: "" },
    /** Column seams for the press. Left off, columns that are blank in every
        row are used, and art with no such column presses as one block. */
    letterBounds: { type: Array, default: null },
});

const FRAME_MS = 50; // about 20 fps while a motion runs
const PASS_MS = 1800; // one sandstorm crossing
const BAND = 9; // columns of turbulence behind the storm front
const INTRO_MS = 300; // beat before the reveal starts
const GLYPHS = " *+=:<>[]{}'\"?^~$";

// Rows are padded to one width before anything is drawn, so the block keeps
// its size between rest and a running motion (trailing spaces are invisible,
// a half-column jump is not). The spare column on the right is for the
// press, which moves a letter's face one column over.
const grid = computed(() => {
    const raw = props.text.split("\n");
    const width = raw.reduce((w, r) => Math.max(w, r.length), 0) + 1;
    const lines = raw.map((r) => r.padEnd(width));
    return {
        lines,
        width,
        rows: lines.length,
        rest: lines.join("\n"),
        blank: lines.map(() => " ".repeat(width)).join("\n"),
    };
});

function seams(lines, width) {
    const given = props.letterBounds;
    if (given && given.length >= 2) {
        const b = given.filter((c) => c > 0 && c < width);
        return [0, ...b, width];
    }
    if (props.text === SKAPI_BANNER) return SKAPI_BOUNDS;
    const found = [0];
    for (let c = 1; c < width; c++) {
        if (lines.every((l) => l[c] === " ") && !lines.every((l) => l[c - 1] === " ")) found.push(c);
    }
    found.push(width);
    return found.length > 2 ? found : [0, width];
}

// Each letter of the art in three states. This face is extruded: the `#`
// strokes are the top face and the `\ | / _` characters are the 3D bezel
// (right and bottom). Pressing sinks a letter from full depth to shallow
// (the bezel thins to dots) to pressed (no bezel, and the face moved one
// column right and one row down onto the footprint the bezel used to draw,
// so it reads as pushed into the page), then releases it the same way.
const BEZEL = /[\\|/_<]/g;
const letters = computed(() => {
    const { lines, width } = grid.value;
    const bounds = seams(lines, width);
    return bounds.slice(0, -1).map((from, i) => {
        const to = bounds[i + 1];
        const rows = lines.map((l) => l.slice(from, to));
        const empty = " ".repeat(to - from);
        const flat = rows.map((r) => r.replace(BEZEL, " "));
        return {
            rows,
            shallow: rows.map((r) => r.replace(BEZEL, ".")),
            // the column and row pushed off the block are bezel or blank in
            // this face, never a `#`
            pressed: [empty, ...flat.slice(0, -1)].map((r) => " " + r.slice(0, -1)),
        };
    });
});

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Starting blank rather than swapping to blank on mount: the reveal has to
// own the first paint or the finished art would flash first.
const shown = ref(props.reveal && !reduced() ? grid.value.blank : grid.value.rest);

let timer = 0;
let frame = 0;

// Every motion lands back on the finished art, and the next one follows
// after a beat.
function finish() {
    shown.value = grid.value.rest;
    if (props.loop) timer = window.setTimeout(nextMotion, props.restMs);
}

// The sandstorm. A diagonal front crosses the art left to right; inside the
// band behind it the glyphs scatter into random symbols and gaps, most
// turbulent at the leading edge, and settle back exactly where they were.
// Revealing, everything the front has not reached yet is blank instead, so
// the pass writes the art in rather than disturbing art already there.
function sweep(revealing) {
    const { lines, width, rows } = grid.value;
    // the front runs along p = col + row / 2, a slight diagonal
    const span = width + rows * 0.5 + BAND;
    const start = performance.now();
    let lastDraw = 0;
    const tick = (now) => {
        const t = now - start;
        if (t >= PASS_MS) return finish();
        if (now - lastDraw >= FRAME_MS) {
            lastDraw = now;
            const front = (t / PASS_MS) * span;
            shown.value = lines
                .map((line, row) =>
                    Array.from(line, (ch, col) => {
                        if (ch === " ") return " ";
                        const behind = front - (col + row * 0.5); // 0 at the leading edge
                        if (behind < 0) return revealing ? " " : ch;
                        if (behind >= BAND) return ch;
                        const intensity = 1 - behind / BAND;
                        if (Math.random() > 0.15 + 0.85 * intensity) return ch;
                        return Math.random() < 0.3 ? " " : GLYPHS[(Math.random() * GLYPHS.length) | 0];
                    }).join(""),
                )
                .join("\n");
        }
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
}

// Frame-quantized so every letter gets the identical rhythm: one frame of
// dots on the way down, six pressed, one of dots on the way up, and letters
// starting exactly three frames apart. (A continuous curve sampled at 20 fps
// put stage changes between frames, so the bezel and the face drop landed on
// different frames from letter to letter.)
const PRESS_SEQUENCE = ["shallow", "pressed", "pressed", "pressed", "pressed", "pressed", "pressed", "shallow"];
const PRESS_STAGGER = 3;

function press() {
    const blocks = letters.value;
    const { lines } = grid.value;
    const total = PRESS_STAGGER * (blocks.length - 1) + PRESS_SEQUENCE.length;
    const start = performance.now();
    let drawn = -1;
    const tick = (now) => {
        const f = Math.floor((now - start) / FRAME_MS);
        if (f >= total) return finish();
        if (f !== drawn) {
            drawn = f;
            const state = blocks.map((letter, i) => {
                const k = f - i * PRESS_STAGGER;
                return k < 0 || k >= PRESS_SEQUENCE.length ? letter.rows : letter[PRESS_SEQUENCE[k]];
            });
            shown.value = lines.map((_, r) => state.map((b) => b[r]).join("")).join("\n");
        }
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
}

// TV signal glitch. For a short burst, two or three horizontal tear bands
// (runs of one to three rows) slide sideways and one row picks up a hint of
// static, then everything snaps back. The burst is a double hit, frames on,
// a clean gap, frames on again, the way a bad signal stutters.
const JITTER_PATTERN = [1, 1, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1]; // 1 = torn frame
const JITTER_MAX_SHIFT = 3; // columns a torn band slides
const JITTER_STATIC = 0.12; // share of one row's glyphs replaced by static

function slide(row, shift, width) {
    if (shift > 0) return " ".repeat(shift) + row.slice(0, width - shift);
    if (shift < 0) return row.slice(-shift) + " ".repeat(-shift);
    return row;
}

function tornFrame() {
    const { lines, width, rows: rowCount } = grid.value;
    const rows = lines.slice();
    const bands = 2 + ((Math.random() * 2) | 0);
    for (let b = 0; b < bands; b++) {
        const top = (Math.random() * rowCount) | 0;
        const height = 1 + ((Math.random() * 3) | 0);
        const shift = (1 + ((Math.random() * JITTER_MAX_SHIFT) | 0)) * (Math.random() < 0.5 ? -1 : 1);
        for (let r = top; r < Math.min(rowCount, top + height); r++) rows[r] = slide(rows[r], shift, width);
    }
    const noisy = (Math.random() * rowCount) | 0;
    rows[noisy] = Array.from(rows[noisy], (ch) =>
        ch !== " " && Math.random() < JITTER_STATIC ? GLYPHS[(Math.random() * GLYPHS.length) | 0] : ch,
    ).join("");
    return rows.join("\n");
}

function jitter() {
    const start = performance.now();
    let drawn = -1;
    const tick = (now) => {
        const f = Math.floor((now - start) / FRAME_MS);
        if (f >= JITTER_PATTERN.length) return finish();
        if (f !== drawn) {
            drawn = f;
            shown.value = JITTER_PATTERN[f] ? tornFrame() : grid.value.rest;
        }
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
}

// The idle motions, drawn from in random order, one per rest, never the same
// one twice in a row unless it is the only one asked for. The reveal is not
// among them: it runs once, on load.
const MOTIONS = { storm: () => sweep(false), press, jitter };

const playlist = computed(() => {
    const asked = Array.isArray(props.motions) ? props.motions : String(props.motions).split(/[\s,]+/);
    const known = asked.map((n) => String(n).trim().toLowerCase()).filter((n) => n in MOTIONS);
    return known.length ? known : Object.keys(MOTIONS);
});

let lastMotion = "";

function nextMotion() {
    const pool = playlist.value;
    const choices = pool.length > 1 ? pool.filter((n) => n !== lastMotion) : pool;
    lastMotion = choices[(Math.random() * choices.length) | 0];
    MOTIONS[lastMotion]();
}

function stop() {
    window.clearTimeout(timer);
    cancelAnimationFrame(frame);
}

function start() {
    stop();
    if (reduced()) {
        shown.value = grid.value.rest;
        return;
    }
    if (props.reveal) {
        shown.value = grid.value.blank;
        timer = window.setTimeout(() => sweep(true), INTRO_MS);
        return;
    }
    shown.value = grid.value.rest;
    if (props.loop) timer = window.setTimeout(nextMotion, props.restMs);
}

onMounted(start);
onUnmounted(stop);
watch(() => [props.text, props.reveal, props.loop], start);
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

/* The look lives here, the placement with whoever uses the component: a page
   sets its own margin through a deeper selector. */
.bq-ascii {
    display: inline-block;
    max-width: 100%;
    margin: 0;
    color: @pink;
    font-family: @mono;
    font-weight: 600;
    font-size: min(1.5rem, calc((100vw - 4rem) / 30));
    line-height: 1.15;
    white-space: pre;
    text-align: left;
    overflow: visible;
    text-shadow: var(--bq-text-shadow);
}
</style>
